// Auto-generated by splitter.py — do not regenerate; edit directly.

import React from 'react';
import { Check, ChevronLeft } from 'lucide-react';

/**
 * The step rail. Pass `onStep` when the wizard has no compulsory gate between steps — the rail
 * then becomes the navigation, so an operator who has only the facts for step 3 can go straight
 * there instead of pressing Continue through two screens they cannot fill. A wizard that DOES
 * gate omits `onStep` and the rail stays a progress indicator, exactly as before.
 *
 * `visited` is how a step reads once it has been seen: without it, "done" can only mean "before
 * the current one", which is false the moment the rail can be jumped around.
 */
export const WizardSteps = ({ steps, current, onStep, visited }) => (
  <div className="flex items-center gap-0 border-b border-[#EEEDEA] -mx-5 px-5 pb-0 mb-4">
    {steps.map((s, i) => {
      const active = s.id === current;
      const done = visited ? (visited.has?.(s.id) ?? visited.includes?.(s.id)) && !active
        : steps.findIndex(x => x.id === current) > i;
      const cls = `px-3 py-2 text-[11.5px] font-medium border-b-2 inline-flex items-center gap-1.5 ${active ? 'border-[#1F2937] text-[#1F2937]' : done ? 'border-transparent text-[#1F6A3A]' : 'border-transparent text-[#9CA3AF]'}`;
      const body = <>{done && <Check className="w-3 h-3" strokeWidth={2.5} />}<span>{i + 1}. {s.label}</span></>;
      return onStep
        ? <button key={s.id} type="button" onClick={() => onStep(s.id)} className={`${cls} hover:text-[#1F2937]`}>{body}</button>
        : <div key={s.id} className={cls}>{body}</div>;
    })}
  </div>
);

// ---- WizardFooter — shared back/cancel/continue/confirm bar

// `note`: what the action will do, said beside it (e.g. "Every matching row · 14 columns").
/**
 * `nextReason` — why the primary button is disabled, said beside it. A greyed "Continue" with no
 * reason leaves the operator guessing which field the step still wants.
 */
/**
 * `secondary` — { label, onClick, disabled? }: a second way to finish from here, beside the primary
 * (e.g. "Create profile" on a step before the last, when nothing further is required).
 */
export const WizardFooter = ({ onBack, onCancel, onNext, nextLabel = 'Continue', nextDisabled, nextReason = null, primaryIcon: Icon, note = null, secondary = null }) => (
  <div className="px-5 py-3 border-t border-[#EEEDEA] bg-[#FBFAF7] -mx-5 -mb-4 mt-4 sticky -bottom-4 z-10 flex items-center justify-between gap-3">
    {onBack ? <button onClick={onBack} className="h-8 px-3 text-[12px] font-medium rounded-md text-[#1F2937] hover:bg-[#F2F1ED] inline-flex items-center gap-1.5"><ChevronLeft className="w-3.5 h-3.5" />Back</button> : <div className="min-w-0 text-[11.5px] text-[#6B7280] truncate">{note}</div>}
    <div className="flex items-center gap-2">
      {nextDisabled && nextReason && <span className="text-[11.5px] text-[#8A5A12]">{nextReason}</span>}
      <button onClick={onCancel} className="h-8 px-3 text-[12px] font-medium rounded-md text-[#1F2937] hover:bg-[#F2F1ED]">Cancel</button>
      {secondary && <button onClick={secondary.onClick} disabled={secondary.disabled} className="h-8 px-3 text-[12px] font-medium rounded-md text-[#1F2937] border border-[#E6E4E1] bg-white hover:bg-[#F2F1ED] disabled:opacity-40 disabled:cursor-not-allowed">{secondary.label}</button>}
      <button onClick={onNext} disabled={nextDisabled} title={nextDisabled && nextReason ? nextReason : undefined} className="h-8 px-3 text-[12px] font-medium rounded-md bg-[#1F2937] hover:bg-[#111827] disabled:bg-[#9CA3AF] disabled:cursor-not-allowed text-white inline-flex items-center gap-1.5">
        {Icon && <Icon className="w-3.5 h-3.5" />}{nextLabel}
      </button>
    </div>
  </div>
);

/* ----------------------------------------------------------------------------
   MANUAL ORDER WIZARD
   ---------------------------------------------------------------------------- */

